import type { Language, TranslationKey, AllTranslations, LanguageData } from './types';

// Import translation files
import enTranslations from './locales/en.json';
import zhHantTranslations from './locales/zh-Hant.json';
import zhHansTranslations from './locales/zh-Hans.json';

// Current language state
let currentLanguage: Language = 'en';

// Consolidated translations object
const translations: AllTranslations = {
  en: enTranslations as LanguageData,
  'zh-Hant': zhHantTranslations as LanguageData,
  'zh-Hans': zhHansTranslations as LanguageData,
};

/**
 * Translate a key with optional parameter substitution
 * @param key Translation key
 * @param params Optional parameters for string interpolation
 * @returns Translated string
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const currentTranslations = translations[currentLanguage] as LanguageData;
  let translation = currentTranslations[key] ?? key;

  if (params) {
    translation = translation.replace(/\$\{(\w+)\}/g, (_match: string, k: string) =>
      Object.prototype.hasOwnProperty.call(params, k) ? String(params[k]) : '',
    );
  }

  return translation;
}

/**
 * Set the current language
 * @param lang Language to set
 */
export function setLanguage(lang: Language): void {
  if (lang in translations) {
    currentLanguage = lang;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }
}

/**
 * Get the current language
 * @returns Current language
 */
export function getLanguage(): Language {
  return currentLanguage;
}

/**
 * Apply translations to elements with data-i18n attributes
 * @param root Root element to search within (defaults to document)
 */
export function applyTranslations(root: ParentNode = document): void {
  const elements = root.querySelectorAll<HTMLElement>('[data-i18n]');
  elements.forEach(el => {
    const key = el.dataset.i18n!;
    el.textContent = t(key);
  });
}

/**
 * Get all available languages
 * @returns Array of available language codes
 */
export function getAvailableLanguages(): Language[] {
  return Object.keys(translations) as Language[];
}

/**
 * Check if a language is supported
 * @param lang Language code to check
 * @returns True if language is supported
 */
export function isLanguageSupported(lang: string): lang is Language {
  return lang in translations;
}

/**
 * Get all translations for a specific language
 * @param lang Language code
 * @returns Translation object for the language
 */
export function getTranslationsForLanguage(lang: Language): LanguageData | null {
  return (lang in translations) ? translations[lang] as LanguageData : null;
}

/**
 * Type-safe translation function (for development)
 * @param key Translation key (type-checked)
 * @param params Optional parameters for string interpolation
 * @returns Translated string
 */
export function ts(key: TranslationKey, params?: Record<string, string | number>): string {
  return t(key, params);
}

// Re-export types for convenience
export type { Language, TranslationKey, LanguageData, AllTranslations } from './types';